/**
 * Export guides (FR-28 – FR-32, FR-72).
 *
 * The export takes days, with a confirmation email in the middle that is easy
 * to miss. So the steps are a checklist the app remembers (FR-28), and the
 * person ticks each one off as they do it (FR-30).
 *
 * The upload is available from the start. It used to appear only at the fourth
 * step, so someone who already had the file had to click through three "I did
 * it" buttons before the app would take it.
 *
 * FR-31 — the instruction text is data from the server with a `last_verified`
 * date shown here, so a change to Amazon's flow is a content edit.
 */
import { Link, useParams } from 'react-router-dom';
import { useRef, useState } from 'react';
import { api, uploadImport } from '../api.js';
import { useApi, useAction } from '../hooks.js';
import { Markdown } from '../markdown.js';
import { ErrorBox, Icon, Notice, PageHead, Skeleton, formatDate } from '../ui.js';
import type { WizardResponse } from '../types.js';

const ORDER = ['not_started', 'requested', 'confirmed', 'delivered', 'imported'];

export function Wizard() {
  const { channel } = useParams<{ channel: string }>();
  const wizard = useApi<WizardResponse>(channel ? `/api/wizards/${channel}` : null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploaded, setUploaded] = useState<string | null>(null);

  const advance = useAction(async (to: string) => {
    await api.post(`/api/wizards/${channel}/advance`, { to });
    wizard.reload();
  });

  const upload = useAction(async (file: File) => {
    const { runId } = await uploadImport(file, channel!);
    setUploaded(runId);
    wizard.reload();
  });

  if (wizard.error) return <ErrorBox error={wizard.error} />;
  if (!wizard.data) return <Skeleton rows={4} />;

  const { state, content, due_nudges } = wizard.data;
  const currentIndex = ORDER.indexOf(state);
  // The last state is "done", not a step to perform.
  const steps = ORDER.slice(0, -1).filter((s) => content.steps[s]);
  const finished = state === 'imported';

  return (
    <>
      <PageHead title={content.title} back={{ to: '/imports', label: 'Imports' }}>
        Steps last checked {formatDate(content.lastVerified, { year: 'always' })}.
      </PageHead>

      {due_nudges.length > 0 ? (
        <Notice kind="warn" title="This has been waiting a while">
          {state === 'requested'
            ? 'Most stalls at this step are an unclicked confirmation email. Check spam.'
            : 'The file has not arrived yet. If it has been more than 36 hours, request it again.'}
        </Notice>
      ) : null}

      {finished ? (
        <Notice
          kind="ok"
          title="The last export is imported"
          actions={
            <button onClick={() => void advance.run('not_started')} disabled={advance.pending}>
              Start a new export
            </button>
          }
        >
          Start again when the next one is due.
        </Notice>
      ) : (
        <div className="card">
          <div className="steps">
            {steps.map((s) => {
              const step = content.steps[s]!;
              const index = ORDER.indexOf(s);
              const isCurrent = index === currentIndex;
              const done = index < currentIndex;
              return (
                <div className={`step${done ? ' done' : isCurrent ? ' current' : ''}`} key={s}>
                  <span className="marker" />
                  <div>
                    <h3>{step.title}</h3>
                    {isCurrent ? (
                      <>
                        <div className="guide">
                          <Markdown source={step.body ?? ''} />
                        </div>
                        <div className="actions" style={{ marginTop: 10 }}>
                          {step.advanceLabel && step.advancesTo ? (
                            <button
                              className="primary"
                              onClick={() => void advance.run(step.advancesTo!)}
                              disabled={advance.pending}
                            >
                              {step.advanceLabel}
                            </button>
                          ) : null}
                          {currentIndex > 0 ? (
                            <button
                              className="quiet"
                              onClick={() => void advance.run(ORDER[currentIndex - 1]!)}
                              disabled={advance.pending}
                            >
                              Back a step
                            </button>
                          ) : null}
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          {advance.error ? <ErrorBox error={advance.error} /> : null}
        </div>
      )}

      <div className="card stack tight">
        <h3>{state === 'delivered' ? 'Upload the file' : 'Already have the file?'}</h3>
        <p className="small muted">
          Upload it at any point. It is checked before anything is imported, and you review every line.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".zip,.csv,.pdf,application/zip,text/csv,application/pdf"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload.run(f);
            e.target.value = '';
          }}
        />
        <div className="actions">
          <button
            className={state === 'delivered' ? 'primary' : undefined}
            onClick={() => fileRef.current?.click()}
            disabled={upload.pending}
          >
            <Icon name="upload" />
            {upload.pending ? 'Uploading…' : 'Choose the export file'}
          </button>
        </div>
        {upload.error ? <ErrorBox error={upload.error} /> : null}
        {uploaded ? (
          <Notice kind="ok" title="Uploaded">
            The file is being checked. That takes under a minute for a CSV and longer for a full export.{' '}
            <Link to={`/imports/${uploaded}`}>Open the import</Link>
          </Notice>
        ) : null}
      </div>

      {content.cadenceNote ? (
        <details className="more">
          <summary>Why this schedule</summary>
          <p className="small muted">{content.cadenceNote}</p>
        </details>
      ) : null}
    </>
  );
}
